import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  Button,
  Dropdown,
  ErrorContainer,
  LabelComponent,
  ReeligibilityInfo,
  Stack,
  StandardCheckbox,
  Tooltip
} from 'components';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { useEffect, useRef, useState } from 'react';
import {
  getCHW,
  getClient,
  getClientHistory,
  getMpi,
  getPrograms,
  putChangeStatus
} from 'store/clients/clientsActions';
import { resetClientHistory, StatusId } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getGoals, getPresets, getTodos } from 'store/roadmaps/roadmapsActions';
import { getTodos as getMyTodos } from 'store/myTodos/myTodosActions';
import * as yup from 'yup';
import clsx from 'clsx';
import { useResponsive } from 'utils/hooks/useMediaQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Avatar from './avatar';
import style from './changeStatusModal.module.scss';

type Props = {
  onClose: () => void;
  onSubmitSuccess?: (data?: any) => void;
  isClientListPage?: boolean;
};

const ChangeStatusModal = ({ onClose, onSubmitSuccess, isClientListPage }: Props) => {
  const { programsList, chwList } = useAppSelector((state) => state.clients);
  const { client, clientHistory } = useAppSelector((state) => state.clients);
  const dispatch = useAppDispatch();
  const [showMoreStatuses, setShowMoreStatuses] = useState(false);
  const { organizationId } = getLsUserRoleObject();
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const { enableDateTimeUpdates, enableMpiUpdate } = useFlags();
  const { isMobile } = useResponsive();
  const isMobileMPIUpdate = isMobile && enableMpiUpdate;
  const textRefs = {
    programNames: useRef<(HTMLSpanElement | null)[]>([]),
    chwNames: useRef<(HTMLSpanElement | null)[]>([])
  };

  const activePrograms = programsList.filter((program) => program.active);

  const [truncatedStates, setTruncatedStates] = useState<{
    programNames: { [key: number]: boolean };
    chwNames: { [key: number]: boolean };
  }>({
    programNames: {},
    chwNames: {}
  });
  const initialStatus = client.data?.status?.id || '';

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      status: initialStatus as StatusId,
      programId: client.data?.programId?.toString() || null,
      chwId: client.data?.chwId?.toString() || null,
      confirmGraduated: false,
      confirmWithdrawn: false,
      confirmLostToFollowUp: false,
      confirmTerminatedForSafety: false,
      confirmOptOut: false
    },
    validationSchema: yup.object().shape({
      status: yup.string(),
      programId: yup.string().nullable().required('Program is required.'),
      chwId: yup
        .string()
        .nullable()
        .when('status', {
          is: (val: StatusId) => {
            if (!val || val === client.data?.status?.id) {
              return client.data?.status?.chwRequired;
            }
            const found = client.data?.eligibleStatuses.find((s) => s.id === val);
            return found?.chwRequired;
          },
          then: (schema) => schema.required('CHW is required')
        }),
      confirmGraduated: yup.boolean().when('status', {
        is: (status: string) => status === 'GRADUATED' && status !== initialStatus,
        then: (schema) => schema.oneOf([true], 'Required')
      }),
      confirmWithdrawn: yup.boolean().when('status', {
        is: (status: string) => status === 'WITHDRAWN' && status !== initialStatus,
        then: (schema) => schema.oneOf([true], 'Required')
      }),
      confirmLostToFollowUp: yup.boolean().when('status', {
        is: (status: string) => status === 'LOST_TO_FOLLOW_UP' && status !== initialStatus,
        then: (schema) => schema.oneOf([true], 'Required')
      }),
      confirmTerminatedForSafety: yup.boolean().when('status', {
        is: (status: string) => status === 'TERMINATED_FOR_SAFETY' && status !== initialStatus,
        then: (schema) => schema.oneOf([true], 'Required')
      }),
      confirmOptOut: yup.boolean().when('status', {
        is: (status: string) => status === 'OPT_OUT' && status !== initialStatus,
        then: (schema) => schema.oneOf([true], 'Required')
      })
    }),
    onSubmit: async (values) => {
      const statusInfo = {
        id: client.data?.id,
        status: {
          id: Boolean(values.status) ? values.status : client.data?.status.id
        },
        programId: values.programId,
        chwId: chwList.some((a: any) => Number(a.userId) === Number(values.chwId))
          ? values.chwId
          : null
      };

      const response = await dispatch(
        putChangeStatus({ clientId: client.data?.id ?? '', organizationId, statusInfo })
      );

      if (response.meta.requestStatus === 'fulfilled') {
        if (!isClientListPage) {
          dispatch(
            getClient({ clientId: client.data?.id ?? '', organizationId, skipLoading: true })
          );
          dispatch(getClientHistory({ clientId: client.data?.id ?? '' }));
          if (values.status === 'ENROLLED') {
            dispatch(getMpi({ clientId: client.data?.id ?? '' }));
          }
        }
        dispatch(getTodos({ clientId: client.data?.id ?? '' }));
        dispatch(getGoals({ clientId: client.data?.id ?? '' }));
        dispatch(getPresets({ clientId: client.data?.id ?? '' }));
        dispatch(getMyTodos());
        onClose();
        onSubmitSuccess?.({ statusInfo, client: client.data });
      }
    }
  });

  useEffect(() => {
    dispatch(getCHW({ programId: formik.values.programId }));
  }, [formik.values.programId]);

  useEffect(() => {
    dispatch(getPrograms({ chwId: formik.values.chwId }));
  }, [formik.values.chwId]);

  useEffect(() => {
    if (client.data?.id && client.data?.id !== '') {
      dispatch(getClientHistory({ clientId: client.data?.id ?? '' }));
    }
    return () => {
      dispatch(resetClientHistory());
    };
  }, [client.data?.id]);

  const mainStatuses =
    client.data?.eligibleStatuses?.filter((status) => status.priority === 'MAIN') ?? [];

  const secondaryStatuses =
    client.data?.eligibleStatuses?.filter((status) => status.priority === 'SECONDARY') ?? [];

  const statuses = showMoreStatuses ? [...mainStatuses, ...secondaryStatuses] : mainStatuses;

  const hideFirstCheckmark = formik.values.status !== client.data?.status?.id;

  useEffect(() => {
    const checkTruncation = () => {
      requestAnimationFrame(() => {
        const newTruncatedStates = {
          programNames: {} as { [key: number]: boolean },
          chwNames: {} as { [key: number]: boolean }
        };

        textRefs.programNames.current.forEach((el, index) => {
          if (el) {
            newTruncatedStates.programNames[index] = el.scrollWidth > el.clientWidth;
          }
        });

        textRefs.chwNames.current.forEach((el, index) => {
          if (el) {
            newTruncatedStates.chwNames[index] = el.scrollWidth > el.clientWidth;
          }
        });

        setTruncatedStates(newTruncatedStates);
      });
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    return () => window.removeEventListener('resize', checkTruncation);
  }, [clientHistory.content]);

  useEffect(() => {
    const checkTruncation = () => {
      requestAnimationFrame(() => {
        const newTruncatedStates = {
          programNames: {} as { [key: number]: boolean },
          chwNames: {} as { [key: number]: boolean }
        };

        textRefs.programNames.current.forEach((el, index) => {
          if (el) {
            newTruncatedStates.programNames[index] = el.scrollWidth > el.clientWidth;
          }
        });

        textRefs.chwNames.current.forEach((el, index) => {
          if (el) {
            newTruncatedStates.chwNames[index] = el.scrollWidth > el.clientWidth;
          }
        });

        setTruncatedStates(newTruncatedStates);
      });
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    return () => window.removeEventListener('resize', checkTruncation);
  }, [clientHistory.content]);

  if (!client) {
    return null;
  }

  return (
    <Stack width="100%" height="100%" className={enableMpiUpdate ? style.modalRoot : undefined}>
      <Stack
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="14px"
        bgcolor="#188AA1"
        paddingTop="12px"
        paddingBottom="12px"
      >
        <p className={style.name}>{`${client.data?.firstName} ${client.data?.lastName}`}</p>
        <Stack direction="row" alignItems="center" sx={{ lineHeight: '14px' }}>
          <p className={style.status}>{client?.data?.status?.name}</p>
          {client?.data?.reEligibility?.available && (
            <ReeligibilityInfo
              color="white"
              padding="8px 0 0 4px"
              text={`On ${dayjs(client?.data?.reEligibility?.date).format(
                'MM/DD/YYYY'
              )} this client became eligible for ${client?.data?.reEligibility?.programName}`}
            />
          )}
        </Stack>
      </Stack>
      <Stack className={enableMpiUpdate ? style.scrollAreaRoot : undefined}>
        <form
          onSubmit={formik.handleSubmit}
          style={{ background: '#fff' }}
          className={enableMpiUpdate ? style.formFill : undefined}
        >
          <Stack
            display="flex"
            maxHeight="636px"
            height="auto"
            className={enableMpiUpdate ? style.formBody : undefined}
          >
            <Stack
              display="flex"
              flexGrow="1"
              overflow="auto"
              padding="24px 24px 1px 24px"
              className={enableMpiUpdate ? style.mainSection : undefined}
            >
              <Stack display="flex" flexDirection="row" gap="24px">
                <Stack width="100%" flex="45">
                  <Stack marginBottom="4px">
                    <p className={style.updateStatus}>Update client status</p>
                  </Stack>
                  <ToggleButtonGroup
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '4px',
                      '&.MuiToggleButtonGroup-root': {
                        width: '100%',
                        ...(isMobileMPIUpdate ? { display: 'flex', flexDirection: 'column' } : {})
                      },
                      '&.MuiToggleButtonGroup-root div': {
                        width: '100%'
                      },
                      '&.MuiToggleButtonGroup-root > button': {
                        marginLeft: '0px !important',
                        width: '100%'
                      }
                    }}
                    value={formik.values.status}
                    exclusive
                    onChange={(_, value) => {
                      if (!value && client.data?.status.id) {
                        formik.setFieldValue('status', client.data?.status.id);
                        return;
                      }
                      formik.setFieldValue('status', value);
                    }}
                    aria-label="Client status"
                  >
                    {statuses.map((status) => (
                      <ToggleButton
                        sx={{
                          border: '1px solid #CBD4E1 !important',
                          borderRadius: '4px !important',
                          color: '#314157',
                          textTransform: 'none',
                          fontSize: '16px',
                          lineHeight: '140%',
                          padding: '11px 0',
                          '&.MuiToggleButton-root:hover:not(.Mui-selected)': {
                            backgroundColor: 'transparent'
                          },
                          '&.MuiToggleButton-root:hover': {
                            backgroundColor: '#EFF9FB'
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#EFF9FB',
                            color: '#096F84',
                            border: '1px solid #5EBBCE !important'
                          }
                        }}
                        value={status.id}
                        key={status.id}
                      >
                        <Tooltip
                          disableInteractive
                          enterDelay={300}
                          toolTipText={status.description}
                        >
                          <span>{status.name}</span>
                        </Tooltip>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Stack display="flex" alignItems="center">
                    <Stack display="flex" width="fit-content">
                      <div
                        className={style.toggleButton}
                        onClick={() => setShowMoreStatuses((state) => !state)}
                      >
                        {showMoreStatuses ? 'Hide secondary statuses' : 'See all statuses'}
                      </div>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
              <Stack marginTop="16px" display="flex" gap="8px">
                <Stack width="100%" className={style.selectors}>
                  <LabelComponent mandatory text="Program" />
                  <Dropdown
                    className={style.dropdown}
                    name="programId"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.programId ?? ''}
                    data={activePrograms}
                    label="Select"
                    withTooltip={82}
                  />
                  <ErrorContainer
                    visible={formik.touched.programId && formik.errors.programId}
                    errors={formik.errors}
                    name="programId"
                  />
                </Stack>
                <Stack width="100%" className={style.selectors}>
                  <LabelComponent
                    mandatory={
                      formik.values.status !== client.data?.status?.id
                        ? client.data?.eligibleStatuses.find((s) => s.id === formik.values.status)
                            ?.chwRequired
                        : client.data?.status?.chwRequired
                    }
                    text="CHW"
                  />
                  <Dropdown
                    className={style.dropdown}
                    name="chwId"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.chwId ?? ''}
                    data={chwList.map((chw) => ({
                      id: chw.userId,
                      name: chw.name
                    }))}
                    label="Select"
                  />
                  <ErrorContainer
                    visible={formik.touched.chwId && formik.errors.chwId}
                    errors={formik.errors}
                    name="chwId"
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack
              display="flex"
              gap="12px"
              padding="0 24px 20px 25px"
              borderTop="1px solid #F1F5F9"
              className={enableMpiUpdate ? style.checkboxAndHistorySection : undefined}
            >
              {(['GRADUATED'] as StatusId[]).includes(formik.values.status) &&
                hideFirstCheckmark && (
                  <Stack marginTop="11px">
                    <StandardCheckbox
                      customClass={style.confirmTextCheckbox}
                      value={formik.values.confirmGraduated}
                      name="confirmGraduated"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={
                        <span className={style.confirmText}>
                          Moving this client to <span className={style.highlight}>Graduated</span>{' '}
                          means they will no longer be actively enrolled.
                        </span>
                      }
                    />
                  </Stack>
                )}
              {(['WITHDRAWN'] as StatusId[]).includes(formik.values.status) &&
                hideFirstCheckmark && (
                  <Stack marginTop="11px">
                    <StandardCheckbox
                      customClass={style.confirmTextCheckbox}
                      value={formik.values.confirmWithdrawn}
                      name="confirmWithdrawn"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={
                        <span className={style.confirmText}>
                          Moving this client to <span className={style.highlight}>Withdrawn</span>{' '}
                          means they will no longer be actively enrolled.
                        </span>
                      }
                    />
                  </Stack>
                )}
              {(['LOST_TO_FOLLOW_UP'] as StatusId[]).includes(formik.values.status) &&
                hideFirstCheckmark && (
                  <Stack marginTop="11px">
                    <StandardCheckbox
                      customClass={style.confirmTextCheckbox}
                      value={formik.values.confirmLostToFollowUp}
                      name="confirmLostToFollowUp"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label={
                        <span className={style.confirmText}>
                          Moving this client to{' '}
                          <span className={style.highlight}>Lost to Follow-up</span> means they will
                          no longer be actively enrolled.
                        </span>
                      }
                    />
                  </Stack>
                )}
              {(['OPT_OUT'] as StatusId[]).includes(formik.values.status) && hideFirstCheckmark && (
                <Stack marginTop="11px">
                  <StandardCheckbox
                    customClass={style.confirmTextCheckbox}
                    value={formik.values.confirmOptOut}
                    name="confirmOptOut"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={
                      <span className={style.confirmText}>
                        <span className={style.highlight}>Opt Out</span> is for clients who do not
                        want to be contacted again. When you select this, the app will never
                        automatically move them back to pre-outreach.
                      </span>
                    }
                  />
                </Stack>
              )}
              <Stack>
                {(['TERMINATED_FOR_SAFETY'] as StatusId[]).includes(formik.values.status) &&
                  hideFirstCheckmark && (
                    <Stack marginTop="16px">
                      <StandardCheckbox
                        customClass={style.confirmTerminateForSafety}
                        value={formik.values.confirmTerminatedForSafety}
                        name="confirmTerminatedForSafety"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label={
                          <span>
                            <span className={style.highlight}>Leave a safety note</span> to let your
                            team know why you are terminating this client permanently from all
                            programs.
                          </span>
                        }
                      />
                    </Stack>
                  )}
              </Stack>
              <Stack width="100%" display="flex" flexDirection="row" justifyContent="space-between">
                <button
                  style={{
                    visibility:
                      !clientHistory.content || clientHistory.content.length < 1
                        ? 'hidden'
                        : 'initial'
                  }}
                  type="button"
                  onClick={() => setHistoryExpanded((prev) => !prev)}
                  className={style.historyButton}
                >
                  <span className="material-icons" data-open={historyExpanded}>
                    keyboard_arrow_down
                  </span>
                  History
                </button>
                <Stack width="130px">
                  <Button
                    customStyleClass={style.confirmBtn}
                    type="submit"
                    theme="primary"
                    text="Confirm"
                    disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
                  />
                </Stack>
              </Stack>
              <Stack
                data-open={historyExpanded}
                className={clsx(style.history, enableDateTimeUpdates && style.historyWithTime)}
                display="flex"
                flexDirection="column"
                overflow="auto"
                gap="8px"
              >
                {clientHistory.content.map((item, index: number) => (
                  <Stack
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="6px"
                    boxSizing="border-box"
                    width="100%"
                    key={item.changedAt}
                  >
                    <p className={style.changedAt}>
                      {dayjs(item.changedAt).format(
                        enableDateTimeUpdates ? 'M/D/YYYY h:mm a' : 'M/D/YYYY'
                      )}
                    </p>
                    <p className={style.historyStatus}>{item.status.name}</p>
                    {item.reEligibility?.available && (
                      <ReeligibilityInfo
                        text={`On ${dayjs(item.reEligibility?.date).format(
                          'MM/DD/YYYY'
                        )} this client became eligible for ${item.reEligibility?.programName}`}
                      />
                    )}
                    <div className={style.historyProgram}>
                      <Tooltip
                        toolTipText={item.programName}
                        disableHoverListener={!truncatedStates.programNames[index]}
                        blackTooltip
                        styles={{ marginBottom: '0 !important' }}
                        placement="top"
                      >
                        <span ref={(el) => (textRefs.programNames.current[index] = el)}>
                          {item.programName}
                        </span>
                      </Tooltip>
                    </div>
                    <div className={style.historyChw}>
                      <Tooltip
                        toolTipText={item.chwName}
                        disableHoverListener={!truncatedStates.chwNames[index]}
                        blackTooltip
                        styles={{ marginBottom: '0 !important' }}
                        placement="top"
                      >
                        <span ref={(el) => (textRefs.chwNames.current[index] = el)}>
                          {item.chwName ? `${item.chwName}, CHW` : ''}
                        </span>
                      </Tooltip>
                    </div>
                    <Stack
                      className={style.author}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap="4px"
                    >
                      <Avatar imageUrl={item.author.profileImageKey} />
                      <p>{item.author.name}</p>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};

export default ChangeStatusModal;
